import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { filter, map } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class Auth {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly user = signal<User | null>(null);
  private readonly initialized = signal(false);

  public readonly isAuthenticated = computed(() => this.user() !== null);
  public readonly isInitialized = this.initialized.asReadonly();

  constructor() {
    this.oidcSecurityService.checkAuth().subscribe((res) => {
      const { isAuthenticated, accessToken, userData, idToken, errorMessage } = res;
      if (errorMessage) throw new Error(errorMessage);

      if (isAuthenticated) {
        this.user.set({ accessToken, idToken, userData });
      } else {
        this.user.set(null);
      }
      this.initialized.set(true);
    });
  }

  isAuthenticated$() {
    return toObservable(this.initialized).pipe(
      filter((initialized) => initialized),
      map(() => this.isAuthenticated()),
    );
  }

  login(): void {
    this.oidcSecurityService.authorize();
  }

  logout(): void {
    // Workaround for angular-auth-oidc-client bug where logoff() incorrectly passes id_token_hint
    // Using logoffLocal() + manual redirect until library bug is fixed.
    // See: https://github.com/damienbod/angular-auth-oidc-client/issues/1396

    this.oidcSecurityService.logoffLocal();
    const logoutUrl = `${environment.cognito.domain}/logout?client_id=${environment.cognito.clientId}&logout_uri=${encodeURIComponent(environment.cognito.redirectUrl)}`;
    window.location.href = logoutUrl;
  }

  getAccessToken(): string | null {
    return this.user()?.accessToken ?? null;
  }

  getEmail(): string | null {
    return this.user()?.userData.email ?? null;
  }
}
